import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Box, Building, Check, Maximize, Users, Workflow } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  BliksLoader,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@bliks-io/ui';
import { isNeverInvited } from '@bliks-io/contracts';
import { useInitializeSummary } from '@/api/queries/useInitializeSummary';
import { useDepartments } from '@/api/queries/useDepartments';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useOrganization } from '@/api/queries/useOrganization';
import { OrganigramCanvas } from '@/pages/organization/organigram/components/OrganigramCanvas';
import { WizardStatCard } from '../components/WizardStatCard';
import {
  buildOverviewStats,
  computeDepartmentLevels,
  type OverviewStatKey
} from '../utils/overviewStats';
import type { WizardStepId } from '../wizardSteps';

const STAT_ICONS: Partial<Record<OverviewStatKey, typeof Building>> = {
  departments: Building,
  members: Users,
  functions: Users,
  teams: Users,
  systems: Box,
  processes: Workflow
};

interface OverviewStepProps {
  onNavigateToStep: (step: WizardStepId) => void;
  /** Opt-in: send login invitations to every never-invited member on finish. */
  inviteOnFinish: boolean;
  onInviteOnFinishChange: (value: boolean) => void;
}

export function OverviewStep({
  onNavigateToStep,
  inviteOnFinish,
  onInviteOnFinishChange
}: OverviewStepProps) {
  const { t } = useTranslation();
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  const { data: summary, isLoading } = useInitializeSummary();
  const { data: departments } = useDepartments();
  // The organigram shows the org structure the wizard built — internal members
  // only, matching every data step (ADR 0011); externals are never part of it.
  const { data: orgMembers } = useOrgMembers({
    status: ['inactive', 'pending', 'active'],
    classification: 'internal'
  });
  const { data: organization } = useOrganization();

  if (isLoading || !summary) {
    return (
      <div className="py-12">
        <BliksLoader label={t('common.loading')} />
      </div>
    );
  }

  // Members the wizard created but never invited (status 'inactive'); the
  // opt-in checkbox invites exactly these on completion.
  const neverInvitedCount = (orgMembers ?? []).filter((m) => isNeverInvited(m.status)).length;

  // The blocked alert renders from the always-fresh summary counts — the
  // user sees what Abschließen will reject BEFORE it fails, and a failed
  // Finish invalidates the summary (useWizardSubmit), so there is no
  // snapshot state to go stale on rail/Zurück navigation.
  const blockedInfo = {
    unresolvedDepartments: summary.departments.unresolvedCount,
    usersWithoutDepartment: summary.members.withoutDepartmentCount
  };
  const isBlocked = blockedInfo.unresolvedDepartments > 0 || blockedInfo.usersWithoutDepartment > 0;

  // 'positions' feeds the members/functions subtitles rather than its own card.
  const stats = buildOverviewStats(summary).filter((stat) => stat.key !== 'positions');
  const levels = computeDepartmentLevels(departments ?? []);
  const orgInfo = organization?.name
    ? { name: organization.name, logo: organization.logo }
    : undefined;

  const statSubtitle = (key: (typeof stats)[number]['key']) => {
    switch (key) {
      case 'departments':
        return t('initialSetup.overview.cards.departmentLevels', { count: levels });
      case 'members':
        return t('initialSetup.overview.cards.distinctPositions', {
          count: summary.positions.count
        });
      case 'functions':
        return t('initialSetup.overview.cards.fromPositions', {
          count: summary.positions.count
        });
      case 'processes':
        return t('initialSetup.overview.cards.readyForRecording');
      default:
        return undefined;
    }
  };

  return (
    <div className="px-10 pt-9 pb-6">
      {isBlocked && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-medium">{t('initialSetup.completeBlocked.title')}</p>
              {blockedInfo.unresolvedDepartments > 0 && (
                <p className="text-sm">
                  {t('initialSetup.completeBlocked.unresolvedDepartments', {
                    count: blockedInfo.unresolvedDepartments
                  })}{' '}
                  <button
                    type="button"
                    className="underline font-medium"
                    onClick={() => onNavigateToStep('resolve')}
                  >
                    {t('initialSetup.completeBlocked.goToResolve')}
                  </button>
                </p>
              )}
              {blockedInfo.usersWithoutDepartment > 0 && (
                <p className="text-sm">
                  {t('initialSetup.completeBlocked.usersWithoutDepartment', {
                    count: blockedInfo.usersWithoutDepartment
                  })}{' '}
                  <button
                    type="button"
                    className="underline font-medium"
                    onClick={() => onNavigateToStep('users')}
                  >
                    {t('initialSetup.completeBlocked.goToUsers')}
                  </button>
                </p>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}
      <div className="text-center mb-6.5">
        <span className="inline-flex items-center justify-center w-13 h-13 rounded-full bg-bliks-green-500/14 text-bliks-green-700 mb-3.5">
          <Check className="w-6.5 h-6.5" />
        </span>
        <h2 className="font-display font-extrabold text-[32px] tracking-[-0.02em] text-foreground mb-1.5">
          {t('initialSetup.overview.title')}
        </h2>
        <div className="text-base font-semibold text-foreground">
          {t('initialSetup.overview.subtitle')}
        </div>
        <p className="text-sm text-fg-secondary mt-3 mx-auto max-w-[74ch]">
          {t('initialSetup.overview.description')}
        </p>
      </div>

      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-5.5">
        {stats.map((stat) => {
          const Icon = STAT_ICONS[stat.key];
          return (
            <WizardStatCard
              key={stat.key}
              label={t(`initialSetup.overview.cards.${stat.key}`)}
              value={stat.count}
              icon={Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />}
              sublabel={statSubtitle(stat.key)}
            />
          );
        })}
      </div>

      <div className="rounded-2xl border border-line bg-white p-5.5">
        <div className="flex items-center justify-between mb-4.5">
          <div className="flex items-center gap-2.25">
            <Workflow className="w-4.5 h-4.5 text-bliks-green-700" strokeWidth={1.8} />
            <div>
              <div className="text-[15px] font-bold text-foreground">
                {t('initialSetup.overview.organigram.title')}
              </div>
              <div className="text-xs text-fg-secondary">
                {t('initialSetup.overview.organigram.description')}
              </div>
            </div>
          </div>
          <Button variant="outline" size="small" onClick={() => setIsFullscreenOpen(true)}>
            <Maximize className="w-3.5 h-3.5 mr-2" />
            {t('initialSetup.overview.organigram.fullscreen')}
          </Button>
        </div>
        <div className="h-105 bg-surface-subtle border border-line-subtle rounded-xl overflow-hidden">
          <OrganigramCanvas
            departments={departments ?? []}
            orgMembers={orgMembers}
            organization={orgInfo}
            interactive={false}
            showControls={false}
          />
        </div>
      </div>

      {neverInvitedCount > 0 && (
        <label className="mt-5.5 flex cursor-pointer items-start gap-3 rounded-2xl border border-line bg-white p-5.5">
          <Checkbox
            checked={inviteOnFinish}
            onCheckedChange={(checked) => onInviteOnFinishChange(checked === true)}
            className="mt-0.5"
          />
          <span>
            <span className="block text-[15px] font-bold text-foreground">
              {t('initialSetup.overview.invite.checkboxLabel', { count: neverInvitedCount })}
            </span>
            <span className="mt-1 block text-xs text-fg-secondary">
              {t('initialSetup.overview.invite.helper')}
            </span>
          </span>
        </label>
      )}

      <Dialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('initialSetup.overview.organigram.title')}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            <OrganigramCanvas
              departments={departments ?? []}
              orgMembers={orgMembers}
              organization={orgInfo}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
